import { EmployerContact } from '../models/employers/employer-contact';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { emailValidator } from '../validators/email.validator';
import { phoneNumberWithCountryValidator } from '../validators/phone-number-with-country-validator';
import { AddressFormFactory } from './address-form-factory';
import { EmployerRecordPrimaryContactFormGroup } from '../forms/employer-record-primary-contact-form-group';
import { EmployerRecordAdditionalContactFormGroup } from '../forms/employer-record-additional-contact-form-group';
import { CANADA_COUNTRY_CODE } from '../consts/consts';
import { Address } from '../models/users/dto/address';
import { EmployerContactRequest } from '../models/users/requests/submissionRequests/employer-contact-request';

export class EmployerRecordFormFactory {
  public static buildEmployerPrimaryContactFormGroup(
    employerContact?: EmployerContact
  ): EmployerRecordPrimaryContactFormGroup {
    return new FormGroup(
      {
        id: new FormControl<number | null>(employerContact?.id ?? null),
        hasSigningAuthority: new FormControl(employerContact?.hasSigningAuthority ?? false, { nonNullable: true }),
        firstName: new FormControl<string | null>(employerContact?.firstName ?? null, {
          validators: [Validators.required]
        }),
        lastName: new FormControl<string | null>(employerContact?.lastName ?? null, {
          validators: [Validators.required]
        }),
        jobTitle: new FormControl<string | null>(employerContact?.jobTitle ?? null),
        address: AddressFormFactory.createAddressForm(employerContact?.address ?? undefined),
        email: new FormControl<string | null>(employerContact?.email ?? null, {
          validators: [Validators.required, emailValidator]
        }),
        phoneNumber: new FormControl<string | null>(employerContact?.phoneNumber ?? null, {
          validators: [Validators.required]
        }),
        phoneNumberCountryCode: new FormControl<string | null>(
          employerContact?.phoneNumberCountryCode ?? CANADA_COUNTRY_CODE,
          {
            validators: [Validators.required]
          }
        )
      },
      {
        validators: [phoneNumberWithCountryValidator()]
      }
    );
  }

  public static buildEmployerAdditionalContactFormGroup(
    employerContact?: EmployerContact
  ): EmployerRecordAdditionalContactFormGroup {
    return new FormGroup(
      {
        id: new FormControl<number | null>(employerContact?.id ?? null),
        hasSigningAuthority: new FormControl(employerContact?.hasSigningAuthority ?? false, { nonNullable: true }),
        firstName: new FormControl<string | null>(employerContact?.firstName ?? null, {
          validators: [Validators.required]
        }),
        lastName: new FormControl<string | null>(employerContact?.lastName ?? null, {
          validators: [Validators.required]
        }),
        jobTitle: new FormControl<string | null>(employerContact?.jobTitle ?? null),
        contactTopic: new FormControl<string | null>(employerContact?.contactTopic ?? null, {
          validators: [Validators.required, Validators.maxLength(40)]
        }),
        address: AddressFormFactory.createAddressForm(employerContact?.address ?? undefined),
        email: new FormControl<string | null>(employerContact?.email ?? null, {
          validators: [Validators.required, emailValidator]
        }),
        phoneNumber: new FormControl<string | null>(employerContact?.phoneNumber ?? null, {
          validators: [Validators.required]
        }),
        phoneNumberCountryCode: new FormControl<string | null>(
          employerContact?.phoneNumberCountryCode ?? CANADA_COUNTRY_CODE,
          {
            validators: [Validators.required]
          }
        )
      },
      {
        validators: [phoneNumberWithCountryValidator()]
      }
    );
  }

  public static buildPrimaryEmployerContactRequest(
    formGroup: EmployerRecordPrimaryContactFormGroup
  ): EmployerContactRequest {
    const formValue = formGroup.getRawValue();
    const address = new Address();
    address.addressLine1 = formValue.address.addressLine1 ?? '';
    address.addressLine2 = formValue.address.addressLine2 ?? '';
    address.addressLine3 = formValue.address.addressLine3 ?? '';
    address.city = formValue.address.city ?? '';
    address.stateId = formValue.address.stateId ?? 0;
    address.postalCode = formValue.address.postalCode ?? '';
    address.countryId = formValue.address.countryId ?? 0;

    return {
      employerContactId: formValue.id,
      firstName: formValue.firstName ?? '',
      lastName: formValue.lastName ?? '',
      jobTitle: formValue.jobTitle,
      email: formValue.email ?? '',
      phoneNumberCountryCode: formValue.phoneNumberCountryCode ?? '',
      phoneNumber: formValue.phoneNumber ?? '',
      contactTopic: null,
      hasSigningAuthority: formValue.hasSigningAuthority,
      primaryContact: true,
      address
    } as EmployerContactRequest;
  }

  public static buildAdditionalEmployerContactRequest(
    formGroup: EmployerRecordAdditionalContactFormGroup
  ): EmployerContactRequest {
    const formValue = formGroup.getRawValue();
    let address: Address | null = null;

    if (formValue.hasSigningAuthority) {
      address = new Address();
      address.addressLine1 = formValue.address.addressLine1 ?? '';
      address.addressLine2 = formValue.address.addressLine2 ?? '';
      address.addressLine3 = formValue.address.addressLine3 ?? '';
      address.city = formValue.address.city ?? '';
      address.stateId = formValue.address.stateId ?? 0;
      address.postalCode = formValue.address.postalCode ?? '';
      address.countryId = formValue.address.countryId ?? 0;
    }

    return {
      employerContactId: formValue.id,
      firstName: formValue.firstName ?? '',
      lastName: formValue.lastName ?? '',
      jobTitle: formValue.jobTitle,
      email: formValue.email ?? '',
      phoneNumberCountryCode: formValue.phoneNumberCountryCode ?? '',
      phoneNumber: formValue.phoneNumber ?? '',
      contactTopic: formValue.contactTopic,
      hasSigningAuthority: formValue.hasSigningAuthority,
      primaryContact: false,
      address
    } as EmployerContactRequest;
  }
}
